import React, { useCallback, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/endpoints';
import { isSignInCancelled, signInWith, signOutProviders } from '../services/socialAuth';
import RiderSettings from './RiderSettings';
import { LegalLinks } from './Auth';
import { ProviderLogo } from './ProviderLogos';
import { SUPPORT_URL } from '../config/legal';

/**
 * Account settings.
 *
 * Deleting an account is an App Store requirement (an app offering sign-up must
 * offer deletion in-app) and the right thing to do regardless. It is deliberately
 * two-step: a 24-hour session token is not proof that the person holding the
 * phone is the account owner, so something recent is required first. Linking a
 * new sign-in method demands the same proof, because a linked provider is a
 * permanent way into the account.
 */

type SocialProvider = 'apple' | 'google';
const providerLabel = (provider: SocialProvider) => (provider === 'apple' ? 'Apple' : 'Google');

/** The server's own words for a response we cannot act on, if it sent any. */
const errorFrom = async (response: Response, fallback: string): Promise<string> => {
    const body = await response.json().catch(() => ({}));
    return (body && typeof body.error === 'string' && body.error) || fallback;
};

type Busy = null | 'link-apple' | 'link-google' | 'unlink-apple' | 'unlink-google'
    | 'password' | 'username' | 'logout' | 'logout-all' | 'delete';

const AccountSettings: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
    const { user, token, socialProviders, serverConfig, refreshUser, endSession, logout, logoutError } = useAuth();
    const [busy, setBusy] = useState<Busy>(null);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    // Password accounts confirm the password inline before a provider is linked.
    const [linkPending, setLinkPending] = useState<SocialProvider | null>(null);
    const [linkPassword, setLinkPassword] = useState('');

    const authHeaders = useCallback((extra: Record<string, string> = {}) => ({
        'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...extra,
    }), [token]);

    const run = useCallback(async (label: Busy, work: () => Promise<void>) => {
        setBusy(label); setError(''); setNotice('');
        try { await work(); }
        catch (caught) {
            // Closing the Apple or Google sheet (to re-confirm, or to link) is a
            // choice, not a failure: nothing happened, so nothing is shown.
            if (!isSignInCancelled(caught)) setError(caught instanceof Error ? caught.message : 'Something went wrong');
        }
        finally { setBusy(null); }
    }, []);

    const providers = user?.providers ?? [];

    /**
     * Prove the person is present right now and return the short-lived reauth
     * token the server wants in X-Reauth-Token. Password accounts use the
     * password; others re-confirm through a provider they already have linked.
     */
    const reauthenticate = useCallback(async (password: string): Promise<string> => {
        let response: Response;
        if (user?.hasPassword) {
            response = await fetch(`${API_URL}/api/auth/reauth`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ password }),
            });
        } else {
            const provider = ((user?.providers ?? []).find(value => value === 'apple' || value === 'google') ?? 'google') as SocialProvider;
            const credential = await signInWith(provider);
            response = await fetch(`${API_URL}/api/auth/reauth`, {
                method: 'POST', headers: authHeaders(), // credential already carries its provider; spread first so it cannot be shadowed
                body: JSON.stringify({ ...credential, provider }),
            });
        }
        // Too many attempts: the server says when to try again; a generic
        // "wrong password" here would send the player round in circles.
        if (response.status === 429) throw new Error(await errorFrom(response, 'Too many attempts. Try again later.'));
        if (!response.ok) throw new Error(user?.hasPassword ? 'That password was not correct' : 'Could not confirm it is you');
        return (await response.json()).reauthToken;
    }, [user?.hasPassword, user?.providers, authHeaders]);

    const link = (provider: SocialProvider, password = '') => run(`link-${provider}` as Busy, async () => {
        const proof = await reauthenticate(password);
        // The provider is in the URL; the server's credential schema is strict
        // and refuses a body that names it again.
        const { provider: _named, ...credential } = await signInWith(provider);
        const response = await fetch(`${API_URL}/api/auth/link/${provider}`, {
            method: 'POST', headers: authHeaders({ 'X-Reauth-Token': proof }), body: JSON.stringify(credential),
        });
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            if (response.status === 403 && body?.code === 'REAUTH_REQUIRED') {
                // The proof expired between the two steps: ask again.
                if (user?.hasPassword) { setLinkPending(provider); setLinkPassword(''); }
                throw new Error(body.error || 'Confirm it is you, then try linking again');
            }
            throw new Error(body?.error || 'Could not link that account');
        }
        setLinkPending(null); setLinkPassword('');
        await refreshUser();
        setNotice(`${providerLabel(provider)} linked.`);
    });

    /** Password accounts confirm first; others go straight to the provider sheets. */
    const startLink = (provider: SocialProvider) => {
        setError(''); setNotice('');
        if (user?.hasPassword) { setLinkPending(provider); setLinkPassword(''); return; }
        void link(provider);
    };

    const unlink = (provider: 'apple' | 'google') => run(`unlink-${provider}` as Busy, async () => {
        const response = await fetch(`${API_URL}/api/auth/link/${provider}`, { method: 'DELETE', headers: authHeaders() });
        if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Could not unlink that account');
        void signOutProviders([provider]);
        await refreshUser();
        setNotice('Removed.');
    });

    const setPassword = () => run('password', async () => {
        // Only accounts without a password get here, so this re-confirms through
        // a linked provider. A password is a new way in and needs the same proof
        // as linking one.
        const proof = await reauthenticate('');
        const response = await fetch(`${API_URL}/api/auth/password`, {
            method: 'POST', headers: authHeaders({ 'X-Reauth-Token': proof }), body: JSON.stringify({ password: newPassword }),
        });
        if (!response.ok) throw new Error(await errorFrom(response, 'Could not set a password'));
        setNewPassword(''); await refreshUser();
        setNotice('Password set. You can now sign in with it.');
    });

    const changeUsername = () => run('username', async () => {
        const response = await fetch(`${API_URL}/api/me/username`, {
            method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ username: newUsername }),
        });
        if (!response.ok) throw new Error((await response.json().catch(() => ({}))).error || 'Could not change your name');
        setNewUsername(''); await refreshUser();
        setNotice('Name updated.');
    });

    // The header's sign-out icon is hidden on phones, so this is the way out there.
    const signOut = () => run('logout', logout);

    const logoutEverywhere = () => run('logout-all', async () => {
        await fetch(`${API_URL}/api/auth/logout-all`, { method: 'POST', headers: authHeaders() });
        endSession();
    });

    const deleteAccount = () => run('delete', async () => {
        // Re-authenticate first: a borrowed, still-signed-in phone must not be
        // enough to destroy someone's account.
        const proof = await reauthenticate(deletePassword);

        const response = await fetch(`${API_URL}/api/auth/account`, {
            method: 'DELETE', headers: authHeaders({ 'X-Reauth-Token': proof }),
        });
        if (!response.ok) throw new Error(await errorFrom(response, 'Could not delete your account'));
        endSession();
    });

    if (!user) return null;

    return (
        <div className="account-settings panel">
            <div className="panel-heading">
                <div><p className="section-label">Settings</p><h2>Account</h2></div>
                {onClose && <button className="auth-switch settings-close" onClick={onClose}>Close</button>}
            </div>

            {notice && <div className="auth-notice" role="status">{notice}</div>}
            {error && <div className="auth-error" role="alert">{error}</div>}

            <RiderSettings token={token} />

            {/* Always here, so the way to report is findable even with nobody
                else on the mountain to tap ••• next to. */}
            <section>
                <h3>Report or hide a rider</h3>
                <p className="auth-subtitle">
                    Tap ••• next to another player's rider nickname, in Live bets or under the run, and choose
                    Report rider or Hide rider. You can also report a rider to us by email.
                </p>
                <div className="settings-row">
                    <a className="btn-quiet settings-link" href={`${SUPPORT_URL}#report`} target="_blank" rel="noopener noreferrer">Report by email</a>
                </div>
            </section>

            <section>
                <h3>Your sign-in name</h3>
                <p className="auth-subtitle">This name is private and separate from your rider's shown name.</p>
                {user.canChangeUsername ? (
                    <div className="settings-row">
                        <input className="field" value={newUsername} onChange={event => setNewUsername(event.target.value)}
                            placeholder={user.username} minLength={3} maxLength={20} aria-label="New username" />
                        <button className="btn-settings" disabled={busy !== null || newUsername.length < 3} onClick={changeUsername}>
                            {busy === 'username' ? 'Saving…' : 'Change once'}
                        </button>
                    </div>
                ) : (
                    <p className="auth-subtitle">Signed in as <strong>{user.username}</strong>. Names can be changed once.</p>
                )}
            </section>

            <section>
                <h3>Sign-in methods</h3>
                <ul className="settings-list">
                    <li>
                        <span className="method-name">Password <span className={`method-status${user.hasPassword ? ' is-on' : ''}`}>{user.hasPassword ? 'Set' : 'Not set'}</span></span>
                    </li>
                    {socialProviders.map(provider => {
                        const linked = providers.includes(provider);
                        return (
                            <li key={provider}>
                                <span className="method-name"><ProviderLogo provider={provider} className="method-logo" />{providerLabel(provider)} <span className={`method-status${linked ? ' is-on' : ''}`}>{linked ? 'Linked' : 'Not linked'}</span></span>
                                <button className="btn-quiet" disabled={busy !== null}
                                    onClick={() => (linked ? unlink(provider) : startLink(provider))}>
                                    {busy === `link-${provider}` || busy === `unlink-${provider}` ? 'Working…' : linked ? 'Remove' : 'Link'}
                                </button>
                            </li>
                        );
                    })}
                </ul>
                {linkPending && (
                    <div className="settings-row">
                        <input className="field" type="password" value={linkPassword} autoComplete="current-password"
                            onChange={event => setLinkPassword(event.target.value)}
                            placeholder={`Confirm your password to link ${providerLabel(linkPending)}`} aria-label="Confirm password to link" />
                        <button className="btn-settings" disabled={busy !== null || linkPassword.length === 0} onClick={() => link(linkPending, linkPassword)}>
                            {busy === `link-${linkPending}` ? 'Linking…' : 'Confirm and link'}
                        </button>
                        <button className="btn-quiet" onClick={() => { setLinkPending(null); setLinkPassword(''); }}>Cancel</button>
                    </div>
                )}
                {!user.hasPassword && (
                    <div className="settings-row">
                        <input className="field" type="password" value={newPassword} autoComplete="new-password"
                            onChange={event => setNewPassword(event.target.value)} placeholder="Add a password" minLength={8} aria-label="New password" />
                        <button className="btn-settings" disabled={busy !== null || newPassword.length < 8} onClick={setPassword}>
                            {busy === 'password' ? 'Saving…' : 'Set password'}
                        </button>
                    </div>
                )}
            </section>

            <section>
                <h3>Sign out</h3>
                <p className="auth-subtitle">Sign out on this device. You stay signed in anywhere else.</p>
                <div className="settings-row">
                <button className="btn-quiet" disabled={busy !== null} onClick={signOut}>
                    {busy === 'logout' ? 'Signing out…' : 'Sign out'}
                </button>
                </div>
                {logoutError && <div className="auth-error" role="alert">{logoutError}</div>}
            </section>

            <section>
                <h3>Security</h3>
                <p className="auth-subtitle">Signed in on a phone you no longer have? End every session at once.</p>
                <div className="settings-row">
                <button className="btn-quiet" disabled={busy !== null} onClick={logoutEverywhere}>
                    {busy === 'logout-all' ? 'Signing out…' : 'Sign out on all devices'}
                </button>
                </div>
            </section>

            <section className="settings-danger">
                <h3>Delete account</h3>
                <p className="auth-subtitle">
                    Your profile, email and linked sign-ins are removed. Past rounds stay in the
                    published fairness record without your name attached.
                </p>
                {!confirmDelete ? (
                    <div className="settings-row"><button className="btn-quiet is-danger" onClick={() => setConfirmDelete(true)}>Delete my account…</button></div>
                ) : (
                    <div className="settings-row">
                        {user.hasPassword && (
                            <input className="field" type="password" value={deletePassword} autoComplete="current-password"
                                onChange={event => setDeletePassword(event.target.value)} placeholder="Confirm your password" aria-label="Confirm password" />
                        )}
                        <button className="btn-settings btn-danger" disabled={busy !== null} onClick={deleteAccount}>
                            {busy === 'delete' ? 'Deleting…' : 'Permanently delete'}
                        </button>
                        <button className="btn-quiet" onClick={() => setConfirmDelete(false)}>Cancel</button>
                    </div>
                )}
            </section>

            <footer className="settings-legal">
                <LegalLinks serverConfig={serverConfig} />
            </footer>
        </div>
    );
};

export default AccountSettings;
